import {
  throwError as observableThrowError,
  Observable,
  forkJoin,
  of,
} from "rxjs";

import { catchError, tap } from "rxjs/operators";
import {
  Injectable,
  EventEmitter,
  Output,
  Inject,
  OnDestroy,
} from "@angular/core";
// import { Http, Headers, Response} from '@angular/http'
import { HttpClient, HttpHeaders, HttpResponse } from "@angular/common/http";
import { AuthService } from "./auth.service";
import {
  FieldMetadataDetail,
  MetadataSet,
  MetadataEntity,
  GenericDataEntity,
} from "../insa-models/meta-data.model";
import { appStateProvider } from "../insa-providers/AppStateProvider";
import { appConfig } from "../../insaweb.config";
import { LocalizeService } from "./localize.service";
import { SharedMethodsModule } from "../insa-custom-scripts/sharedMethods";
import { isString } from "util";

@Injectable()
export class InsaMetadataService implements OnDestroy {
  private language = "en";
  private hostAddress = appConfig.insaServiceAddress;
  private servicesURL = "/InsaMetaData";
  private metadataEntityCache: { [key: string]: MetadataEntity } = {};
  authServiceSubscription;
  stateSubscription;
  metadataSet: MetadataSet;

  @Output() MetaDataEvent: EventEmitter<MetadataSet> =
    new EventEmitter<MetadataSet>(true);
  @Output() GenericDataEvent: EventEmitter<any> = new EventEmitter<any>(true);

  constructor(
    private _appStateProvider: appStateProvider,
    private http: HttpClient,
    private _authService: AuthService,
    private _localize: LocalizeService,
  ) {
    this.language = this._localize.getLocale().substring(0, 2);
    this.stateSubscription = _appStateProvider.subscribe((eventdata) =>
      this.initDataFetching(eventdata),
    );
    this.authServiceSubscription =
      this._authService.LoggedInStatusChangedEmitter.subscribe((loggedIn) => {
        if (loggedIn.value === false) {
          this.language = this._localize.getLocale().substring(0, 2);
        }
      });
  }

  get metadataName() {
    if (!this.metadataSet) return null;
    return this.metadataSet[0].InsaMetaData[0].MetaDataName;
  }

  initDataFetching(e) {
    this.language = this._localize.getLocale().substring(0, 2);
  }

  getMetaDataInformation(metaDataName: string, fromDate?: Date, toDate?: Date) {
    let headers = this._authService.getAuthHeaders("application/json");

    const language = this._localize.getLocale();

    const noCache = headers.set("Cache-Control", "no-cache, no-store").set("Pragma", "no-cache");

    let metaDataEntity: Observable<MetadataEntity> = this.http
      .get(
        this.hostAddress +
          "/InsaMetaData/MetaData?MetaDataName=" +
          metaDataName +
          "&language=" +
          language +
          "&_t=" +
          Date.now(),
        { headers: noCache },
      )
      .pipe(
        catchError((err) =>
          this.handleError(err, `Could not get metadata for "${metaDataName}"`),
        ),
      ) as Observable<MetadataEntity>;

    const formatDate = (d: any) => {
      if (!d) return "";

      const date = new Date(d);

      if (isNaN(date.getTime())) {
        console.error("FormatDate: Primljeni podatak nije valjan datum", d);
        return "";
      }

      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");

      return `${yyyy}-${mm}-${dd}`;
    };

    let genericDataUrl =
      this.hostAddress +
      "/InsaMetaData/GenericData?MetaDataName=" +
      metaDataName +
      "&language=" +
      language;
    if (fromDate) genericDataUrl += "&fromDate=" + formatDate(fromDate);
    if (toDate) genericDataUrl += "&toDate=" + formatDate(toDate);
    genericDataUrl += "&_t=" + Date.now();

    let genericDataEntity: Observable<GenericDataEntity> = this.http
      .get(genericDataUrl, { headers: noCache })
      .pipe(
        catchError((err) =>
          this.handleError(
            err,
            `Could not get genericData for "${metaDataName}"`,
          ),
        ),
      ) as Observable<GenericDataEntity>;

    let req = forkJoin([metaDataEntity, genericDataEntity]).pipe(
      tap((metadata: MetadataSet) => {
        this.metadataSet = this.makeMetadataSet(metadata);
        this.metadataEntityCache[metaDataName] = this.metadataSet[0];
        this.MetaDataEvent.emit(metadata);
      }),
    );

    return req;
  }

  addGenericEntity(newEntity: any, MetaDataName: string) {
    let headers = this._authService.getAuthHeaders("application/json");
    let body = JSON.stringify(newEntity);

    const url =
      this.hostAddress +
      "/InsaMetaData/GenericData?MetaDataName=" +
      MetaDataName; //holi
    return this.http
      .post(url, body, { headers, reportProgress: true, observe: "events" }) //holi
      .pipe(
        catchError((err) =>
          this.handleError(err, "Could not add entity from " + MetaDataName),
        ),
      );
  }

  updateGenericEntity(updatedEntity: any, MetaDataName: string) {
    let headers = this._authService.getAuthHeaders("application/json");
    let body = JSON.stringify(updatedEntity);
    const url =
      this.hostAddress +
      "/InsaMetaData/GenericData?MetaDataName=" +
      MetaDataName;
    return this.http
      .put(url, body, { headers, reportProgress: true, observe: "events" })
      .pipe(
        catchError((err) =>
          this.handleError(err, "Could not update entity from " + MetaDataName),
        ),
      );
  }
  deleteGenericEntity(entityID: number, MetaDataName: string) {
    let headers = this._authService.getAuthHeaders("application/json");

    const url =
      this.hostAddress +
      "/InsaMetaData/GenericData?entityID=" +
      entityID +
      "&MetaDataName=" +
      MetaDataName;
    return this.http
      .delete(url, { headers })
      .pipe(
        catchError((err) =>
          this.handleError(err, "could not delete entity from " + MetaDataName),
        ),
      );
  }

  getFile(metadataName: string, fieldName: string, entityID: string | number) {
    let headers = this._authService.getAuthHeaders("application/json");

    return this.http
      .get(
        this.hostAddress +
          `/InsaMetaData/GetFileContent?MetaDataName=${metadataName}&FieldName=${fieldName}&EntityID=${entityID}`,
        { headers },
      )
      .pipe(publishReplay(), refCount())
      .pipe(
        catchError((err) =>
          this.handleError(
            err,
            `getFile error: could not get file from ${metadataName}, Id: ${entityID}, FieldName: ${fieldName}`,
          ),
        ),
      );
  }

  private handleError(error: any, message: string) {
    let errMsg = "Server error";
    if (error.error && error.error.message) {
      errMsg = error.error.message;
    } else if (error.message) {
      errMsg = error.message;
    } else if (error.status) {
      errMsg = `${error.status} - ${error.statusText}`;
    }

    SharedMethodsModule.addNotification("#notification", message, false);
    console.error("HTTP Error:", error);
    return observableThrowError(errMsg);
  }

  makeMetadataSet(metadataSet: MetadataSet): MetadataSet {
    const FormMetaData = metadataSet[0].InsaMetaData[0];
    const InsaMetaDataDetails = metadataSet[0].InsaMetaDataDetails;
    const InsaMetadataFormGroups = metadataSet[0].InsaMetadataFormGroups;
    const lookupEntityList = metadataSet[1].MetaDataLookupEntity;
    const recordSet = metadataSet[1].MetaDataSelectedEntity;

    return [
      {
        InsaMetaData: [FormMetaData],
        InsaMetaDataDetails,
        InsaMetadataFormGroups,
      },
      {
        MetaDataLookupEntity: lookupEntityList,
        MetaDataSelectedEntity: recordSet,
      },
    ];
  }

  ngOnDestroy() {
    this.stateSubscription.unsubscribe();
    this.authServiceSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./app/insa-services/insa-metadata.service.ts